import useFacilityUsers from '@UI/Pages/Beneficiaries/Hooks/useFacilityUsers';
import { Message, MultiSelect } from '@verclam/ui-kit';
import * as React from 'react';

type Props = {
	readonly value: readonly number[];
	readonly onChange: (value: readonly number[]) => void;
};

/**
 * Candidates are every active member of the agency directory, plus whichever already-selected ids
 * fall outside that set (an account deactivated after being assigned): dropping them from `options`
 * would silently strip their label from the control without touching `value`, and the current
 * selection would then show as fewer chips than it actually holds.
 */
const AssignedUsersField: React.FC<Props> = ({ value, onChange }) => {
	const { data: facilityUsers, error } = useFacilityUsers();

	const options = React.useMemo(() => {
		if (facilityUsers === null) {
			return [];
		}

		return facilityUsers.list
			.filter((user) => user.isActive || value.includes(user.id))
			.slice()
			.sort((first, second) => first.fullName.localeCompare(second.fullName));
	}, [facilityUsers, value]);

	if (error !== null) {
		return <Message severity="error" text="Impossible de charger les référents de l’agence." />;
	}

	return (
		<MultiSelect<number>
			name="assignedUserIds"
			label="Référents affectés"
			subLabel="L’affectation ouvre l’accès au dossier pour les comptes qui n’ont que la permission « dossiers qui me sont affectés »."
			options={options}
			optionLabel="fullName"
			optionValue="id"
			display="chip"
			filter
			value={value}
			onChange={onChange}
			placeholder="Choisir des référents"
			disabled={facilityUsers === null}
		/>
	);
};

export default AssignedUsersField;
